/**
 * Web Speech API recogniser — the launch default (browser-native, free, no key).
 *
 * The API is still vendor-prefixed in most shipping browsers and is not in TypeScript's DOM
 * library, so the minimal surface we use is declared here rather than pulling in a types package
 * for four interfaces.
 *
 * Two behaviours worth knowing about, both handled below:
 *
 * - **It stops on its own.** The engine ends a session after a pause it judges to be the end of
 *   speech, regardless of whether the player is still holding the key. Push-to-talk therefore
 *   cannot rely on `stop()` being what ends capture; whichever comes first wins, and the result
 *   is delivered exactly once either way.
 * - **`no-speech` is not an error to a player.** Holding the key and saying nothing is a thing
 *   people do. It resolves to an empty transcript, which the caller treats as "unmatched" and
 *   falls back to the authored options.
 */
import {
  SpeechError,
  type SpeechErrorKind,
  type SpeechHandlers,
  type SpeechRecogniser,
} from './stt.js';

/* --- minimal Web Speech typings ----------------------------------------------------------- */

interface WebSpeechAlternative {
  transcript: string;
  confidence: number;
}

interface WebSpeechResult {
  readonly length: number;
  readonly isFinal: boolean;
  item(index: number): WebSpeechAlternative;
  [index: number]: WebSpeechAlternative;
}

interface WebSpeechResultList {
  readonly length: number;
  item(index: number): WebSpeechResult;
  [index: number]: WebSpeechResult;
}

interface WebSpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: WebSpeechResultList;
}

interface WebSpeechErrorEvent extends Event {
  readonly error: string;
  readonly message?: string;
}

interface WebSpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((event: WebSpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
}

type WebSpeechRecognitionCtor = new () => WebSpeechRecognition;

function recognitionCtor(): WebSpeechRecognitionCtor | null {
  const scope = window as unknown as {
    SpeechRecognition?: WebSpeechRecognitionCtor;
    webkitSpeechRecognition?: WebSpeechRecognitionCtor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

export function isWebSpeechAvailable(): boolean {
  return typeof window !== 'undefined' && recognitionCtor() !== null;
}

/** Map the engine's error strings onto something a player can be told. */
function toErrorKind(raw: string): SpeechErrorKind {
  switch (raw) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'permission-denied';
    case 'audio-capture':
      return 'no-microphone';
    case 'no-speech':
      return 'no-speech';
    case 'network':
      return 'network';
    case 'aborted':
      return 'aborted';
    default:
      return 'unknown';
  }
}

export interface WebSpeechOptions {
  /** BCP-47 tag. Follows the UI locale so a Turkish pass gets Turkish recognition for free. */
  lang?: string;
}

export class WebSpeechRecogniser implements SpeechRecogniser {
  readonly id = 'web-speech';

  private recognition: WebSpeechRecognition | null = null;
  private handlers: SpeechHandlers | null = null;
  private best = { transcript: '', confidence: 0 };
  private settled = false;
  private capturing = false;
  private readonly lang: string;

  constructor(options: WebSpeechOptions = {}) {
    this.lang = options.lang ?? 'en-US';
  }

  get available(): boolean {
    return isWebSpeechAvailable();
  }

  get isCapturing(): boolean {
    return this.capturing;
  }

  start(handlers: SpeechHandlers): Promise<void> {
    const Ctor = recognitionCtor();
    if (!Ctor) {
      const error = new SpeechError('unsupported');
      handlers.onError?.(error);
      return Promise.reject(error);
    }

    this.abort();
    this.handlers = handlers;
    this.best = { transcript: '', confidence: 0 };
    this.settled = false;

    const recognition = new Ctor();
    recognition.lang = this.lang;
    // Single utterance: push-to-talk is one answer, not a monologue.
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    this.recognition = recognition;

    return new Promise<void>((resolve, reject) => {
      let started = false;

      recognition.onstart = () => {
        started = true;
        this.capturing = true;
        resolve();
      };

      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i]!;
          const alternative = result[0]!;
          if (result.isFinal) {
            // Keep the most confident final fragment rather than the last one: the engine can
            // emit a confident phrase and then a throwaway one as the room settles.
            if (alternative.confidence >= this.best.confidence || this.best.transcript === '') {
              this.best = {
                transcript: alternative.transcript.trim(),
                confidence: alternative.confidence,
              };
            }
          } else {
            interim += alternative.transcript;
          }
        }

        if (interim) {
          handlers.onPartial?.({ transcript: interim.trim(), confidence: 0, isFinal: false });
        }
      };

      recognition.onerror = (event) => {
        const kind = toErrorKind(event.error);
        // Saying nothing is not an error a player should be shown; it just means no transcript.
        if (kind === 'no-speech' || kind === 'aborted') return;
        const error = new SpeechError(kind, event.message);
        handlers.onError?.(error);
        if (!started) reject(error);
      };

      recognition.onend = () => {
        this.capturing = false;
        this.settle();
        if (!started) {
          // Ended before it ever started: treat as an unusable engine rather than hanging.
          reject(new SpeechError('unknown', 'recognition ended before starting'));
        }
      };

      try {
        recognition.start();
      } catch (cause) {
        // Chrome throws if start() is called while a previous session is still winding down.
        this.capturing = false;
        const error = new SpeechError(
          'unknown',
          cause instanceof Error ? cause.message : undefined,
        );
        handlers.onError?.(error);
        reject(error);
      }
    });
  }

  stop(): void {
    this.recognition?.stop();
  }

  abort(): void {
    const recognition = this.recognition;
    if (!recognition) return;
    // Detach first: an aborted session must not deliver a result.
    this.settled = true;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition.onstart = null;
    try {
      recognition.abort();
    } catch {
      /* already stopped */
    }
    this.recognition = null;
    this.capturing = false;
  }

  dispose(): void {
    this.abort();
    this.handlers = null;
  }

  /** Deliver the final result exactly once, whoever ended the session. */
  private settle(): void {
    if (this.settled) return;
    this.settled = true;
    this.handlers?.onFinal({ ...this.best, isFinal: true });
    this.recognition = null;
  }
}
